import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AgnesButton,
  AgnesCard,
  AgnesCardContent,
  AgnesCardHeader,
  AgnesCardTitle,
  AgnesLabel,
  AgnesPageHeader,
} from '@agnes/ui';
import { ArrowLeft, Copy, RefreshCw } from 'lucide-react';
import { useCentralReprocessamentoStore } from '@/stores/useCentralReprocessamentoStore';
import { confirm } from '@/stores/useConfirm';
import { CentralReprocessamentoMensagemStatusAlert } from '@/components/central-reprocessamento/CentralReprocessamentoMensagemStatusAlert';
import { CentralReprocessamentoFormularioDialog } from '@/components/central-reprocessamento/CentralReprocessamentoFormularioDialog';
import { CENTRAL_REPROCESSAMENTO_TIPOS_ENUM } from '@/constants/central-reprocessamento/ConstantCentralReprocessamentoTipos';
import UtilsMask from '@/utils/UtilsMask';
import UtilsDate from '@/utils/UtilsDate';

const POLL_MS = Number(import.meta.env.VITE_APP_INTERVALO_REQUISICOES_MS || 300000);

type Reproc = any;

function fmt(v?: string) {
  return v ? UtilsDate.format2(v, { toFormat: 'DD/MM/YYYY [às] HH:mm:ss' } as any) : null;
}

export default function PageCentralReprocessamentoDetalhes() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reproc, setReproc] = React.useState<Reproc>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const carregando = useCentralReprocessamentoStore((s) => s.estaBuscandoReprocessamento);
  const duplicando = useCentralReprocessamentoStore((s) => s.estaDuplicandoReprocessamento);
  const resolicitando = useCentralReprocessamentoStore((s) => s.estaResolicitandoReprocessamento);

  React.useEffect(() => {
    if (!id) return;
    (async () => setReproc(await useCentralReprocessamentoStore.getState().buscar(id)))();
    const t = setInterval(
      async () => setReproc(await useCentralReprocessamentoStore.getState().buscar(id, true)),
      POLL_MS,
    );
    return () => clearInterval(t);
  }, [id]);

  const nome = reproc?.id
    ? useCentralReprocessamentoStore.getState().getNomeCompletoReprocessamento(reproc)
    : '';
  const podeResolicitar = reproc?.id
    ? useCentralReprocessamentoStore.getState().verificarPermissaoSolicitacao(reproc)
    : false;
  const duracao = reproc
    ? UtilsDate.duration(reproc?.iniciadoEm, reproc?.finalizadoEm || new Date())
    : '';

  async function onDuplicar() {
    const ok = await confirm({
      message: `Você tem certeza de que deseja duplicar o reprocessamento?<br/><b>${nome}</b>`,
      okLabel: 'Sim, duplicar',
      cancelLabel: 'Não, cancelar',
    });
    if (!ok) return;
    await useCentralReprocessamentoStore.getState().duplicar(reproc);
    navigate('/central-de-reprocessamento');
  }

  return (
    <>
      <AgnesPageHeader
        icon={<RefreshCw />}
        title={nome || 'Detalhes'}
        subtitle="Detalhes do reprocessamento selecionado"
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'Administração' },
          { label: 'eSocial' },
          { label: 'Central de Reprocessamento', href: '/central-de-reprocessamento' },
          { label: nome },
        ]}
      />

      {carregando && <div className="mb-4 text-sm text-agnes-text-muted">Carregando...</div>}

      <AgnesCard className="">
        <AgnesCardHeader className="">
          <AgnesCardTitle>Detalhes</AgnesCardTitle>
        </AgnesCardHeader>

        <AgnesCardContent className="flex items-stretch">
          <div className="grid grid-cols-4 gap-4">
            <Campo label="Nome">
              {reproc?.tipo
                ? CENTRAL_REPROCESSAMENTO_TIPOS_ENUM.get(reproc.tipo).getDescription()
                : null}
            </Campo>
            <Campo label="Raíz CNPJ">{UtilsMask.cnpj(reproc?.nrinsc)}</Campo>
            <Campo label="Duração">{duracao}</Campo>
            <Campo label="Criado por">
              {reproc?.criadoPorUsuario
                ? `${reproc.criadoPorUsuario?.firstName ?? ''} ${reproc.criadoPorUsuario?.lastName ?? ''}`
                : null}
            </Campo>
            <Campo label="Criado em">
              {fmt(reproc?.criadoEm) ?? <em className="text-agnes-text-disabled">Em branco</em>}
            </Campo>
            <Campo label="Iniciado em">
              {fmt(reproc?.iniciadoEm) ?? <em className="text-agnes-text-disabled">Em branco</em>}
            </Campo>
            <Campo label="Finalizado em">
              {fmt(reproc?.finalizadoEm) ?? <em className="text-agnes-text-disabled">Em branco</em>}
            </Campo>
            <Campo label="Atualizado em">
              {fmt(reproc?.atualizadoEm) ?? <em className="text-agnes-text-disabled">Em branco</em>}
            </Campo>
          </div>

          {reproc && (
            <div className="mt-4">
              <CentralReprocessamentoMensagemStatusAlert reprocessamento={reproc} />
            </div>
          )}
        </AgnesCardContent>
      </AgnesCard>

      <div className="flex items-center justify-between mt-4">
        <AgnesButton
          variant="text"
          startIcon={<ArrowLeft className="h-4 w-4" />}
          onClick={() => navigate('/central-de-reprocessamento')}
        >
          Voltar
        </AgnesButton>
        <div className="flex items-center gap-3">
          <AgnesButton
            variant="outlined"
            loading={duplicando}
            startIcon={<Copy className="h-4 w-4" />}
            onClick={onDuplicar}
          >
            Duplicar
          </AgnesButton>
          {podeResolicitar && (
            <AgnesButton
              variant="primary"
              loading={resolicitando}
              startIcon={<RefreshCw className="h-4 w-4" />}
              onClick={() => setDialogOpen(true)}
            >
              Resolicitar
            </AgnesButton>
          )}
        </div>
      </div>

      <CentralReprocessamentoFormularioDialog
        open={dialogOpen}
        id={reproc?.id}
        onClose={() => setDialogOpen(false)}
        onConfirm={async () => {
          setDialogOpen(false);
          if (id) setReproc(await useCentralReprocessamentoStore.getState().buscar(id));
        }}
      />
    </>
  );
}

function Campo({ label, children }: Readonly<{ label: string; children: React.ReactNode }>) {
  return (
    <div className="flex flex-col gap-1">
      <AgnesLabel>{label}</AgnesLabel>
      <div className="text-agnes-text-primary">{children}</div>
    </div>
  );
}
